import { laneAt } from './dragRace';

/**
 * The moments in a race worth stopping for (SPEC-008 PR-8.6).
 *
 * A 400 metre race is twelve seconds of two pictures moving, and a reader who
 * watches it learns the ORDER they finished in and nothing else. The moments are
 * what turn it back into an explanation: the strip freezes three times and says
 * what just happened and why — this one spun for a second and a half, this one
 * took the lead at 180 metres, this one was first to 100 km/h.
 *
 * Every moment is DETECTED FROM THE TRACE rather than authored, so a moment is
 * only ever claimed when it actually occurred. There is no "the F40 launches
 * hard" note waiting to be shown; there is a reading of where both cars were at
 * a given instant, and a sentence composed from it.
 *
 * Selection is by INTEREST, not by chronology. A race has more notable instants
 * than a reader will sit through, so each candidate carries a rank and the best
 * few survive — then they are sorted back into time order, because they are
 * going to be played in it.
 *
 * Pure, and it returns message keys and numbers like every other module here.
 */

/**
 * How many freezes a race gets.
 *
 * Three. Two is not enough to tell a story with a middle; four on a twelve
 * second race means the reader spends more time stopped than moving, and the
 * thing they came to watch stops being a race.
 */
const MAX_MOMENTS = 3;

/**
 * How far apart two freezes have to be, in simulated seconds.
 *
 * Two moments a third of a second apart are one moment reported twice, and
 * stopping twice in a row reads as a stutter rather than as emphasis.
 */
const MINIMUM_SPACING = 1.5;

/** When the launch is judged: late enough to have happened, early enough to matter. */
const LAUNCH_SECONDS = 1.2;

/** Under this a gap is two cars side by side, whatever the arithmetic says. */
const LEVEL_METRES = 3;

/** A slip shorter than this is a tyre working, not a car spinning. */
const NOTABLE_SPIN_SECONDS = 0.4;

/** The longest continuous wheelspin in a lane, as a start and an end. */
function longestSpin(lane) {
    let best = null;
    let start = null;

    lane.samples.forEach((sample, index) => {
        if (sample.wheelspin && start === null) {
            start = sample.seconds;
        }

        const ended = !sample.wheelspin || index === lane.samples.length - 1;

        if (ended && start !== null) {
            const span = { from: start, to: sample.seconds };

            if (!best || span.to - span.from > best.to - best.from) {
                best = span;
            }

            start = null;
        }
    });

    return best;
}

/**
 * The first instant one car went ahead of the other and stayed there.
 *
 * "And stayed there" is the whole difficulty. Two cars trading a metre back and
 * forth off the line cross each other several times, and calling each of those a
 * lead change would fill the race with events nobody watching would describe as
 * one. So a crossing counts only where the new leader is still ahead by a clear
 * margin a second and a half later — which is the difference between a car
 * getting past and a car being briefly beside.
 */
function decisiveLeadChange(lanes) {
    const [first, second] = lanes;
    const aheadAt = (seconds) => laneAt(first, seconds).metres - laneAt(second, seconds).metres;
    const samples = first.samples.filter((sample) => sample.seconds > LAUNCH_SECONDS);
    let previous = Math.sign(aheadAt(LAUNCH_SECONDS));

    for (const sample of samples) {
        const gap = aheadAt(sample.seconds);
        const side = Math.sign(gap);

        if (side !== 0 && side !== previous && previous !== 0) {
            const later = aheadAt(sample.seconds + MINIMUM_SPACING);

            if (Math.sign(later) === side && Math.abs(later) > LEVEL_METRES) {
                return {
                    seconds: sample.seconds,
                    carId: side > 0 ? first.carId : second.carId,
                    metres: Math.abs(laneAt(side > 0 ? first : second, sample.seconds).metres),
                };
            }
        }

        if (side !== 0) {
            previous = side;
        }
    }

    return null;
}

/** Every moment this race could offer, ranked, before any are dropped. */
function candidates(race) {
    const [first, second] = race.lanes;
    const found = [];

    // The launch. Always offered, because every race has one and it is the only
    // moment a reader can predict — which makes it the one worth being right or
    // wrong about before the cars have done anything else.
    const launch = [first, second].map((lane) => laneAt(lane, LAUNCH_SECONDS).metres);
    const launchGap = Math.abs(launch[0] - launch[1]);
    const ahead = launch[0] > launch[1] ? first : second;

    found.push({
        seconds: LAUNCH_SECONDS,
        rank: 4,
        key: launchGap < LEVEL_METRES ? 'launchLevel' : 'launch',
        carId: ahead.carId,
        values: { metres: Math.round(launchGap) },
    });

    // Wheelspin, which is the most explanatory thing that happens on this screen
    // and the only one that is a car failing to use what it has.
    race.lanes.forEach((lane) => {
        const spin = longestSpin(lane);

        if (spin && spin.to - spin.from >= NOTABLE_SPIN_SECONDS) {
            found.push({
                seconds: (spin.from + spin.to) / 2,
                rank: 9,
                key: 'wheelspin',
                carId: lane.carId,
                values: { seconds: (spin.to - spin.from).toFixed(1) },
            });
        }
    });

    const change = decisiveLeadChange(race.lanes);

    if (change) {
        found.push({
            seconds: change.seconds,
            rank: 8,
            key: 'leadChange',
            carId: change.carId,
            values: { metres: Math.round(change.metres) },
        });
    }

    // First to 100 km/h — the catalogue's own headline figure, happening.
    const hundred = race.lanes
        .filter((lane) => lane.zeroToHundred !== null)
        .sort((one, other) => one.zeroToHundred - other.zeroToHundred)[0];

    if (hundred) {
        found.push({
            seconds: hundred.zeroToHundred,
            rank: 5,
            key: 'hundred',
            carId: hundred.carId,
            values: { seconds: hundred.zeroToHundred.toFixed(1) },
        });
    }

    return found;
}

/**
 * The moments this race will actually stop for, in the order they happen.
 *
 * Chosen by rank and then spaced: a moment that lands within `MINIMUM_SPACING`
 * of one already taken is dropped rather than shuffled, because moving it would
 * make it a claim about an instant it was not detected at.
 *
 * Nothing inside the last second of the race is offered either. The finish is
 * already a freeze — the strip stops and the verdict opens — and a hold a
 * heartbeat before it is a hold the reader reads as the end arriving twice.
 */
export function raceMoments(race) {
    if (!race || race.duration <= 0) {
        return [];
    }

    const taken = [];

    candidates(race)
        .filter((moment) => moment.seconds > 0 && moment.seconds < race.duration - 1)
        .sort((one, other) => other.rank - one.rank)
        .forEach((moment) => {
            const crowded = taken.some(
                (kept) => Math.abs(kept.seconds - moment.seconds) < MINIMUM_SPACING,
            );

            if (!crowded && taken.length < MAX_MOMENTS) {
                taken.push(moment);
            }
        });

    return taken.sort((one, other) => one.seconds - other.seconds);
}
